import type { BannerPlacement, ClickReleaseConfig } from '../config/types';
import type { ReleaseEntry } from '../content/types';
import { el } from '../util/dom';
import { enter, leave } from '../anim/transition';

const RESERVE_TRANSITION = 'padding 240ms cubic-bezier(0.22, 1, 0.36, 1)';

/**
 * A full-width banner pinned to the top/bottom of the host page. To minimize cumulative layout
 * shift, it measures its height and reserves that space on `<html>` (animated), then slides into
 * the reserved space — so host content eases down rather than jumping.
 */
export class Banner {
  #root: ShadowRoot;
  #onOpenChangelog: () => void;
  #node: HTMLElement | null = null;
  #open = false;
  #reservedSide: BannerPlacement | null = null;

  constructor(root: ShadowRoot, onOpenChangelog: () => void) {
    this.#root = root;
    this.#onOpenChangelog = onOpenChangelog;
  }

  get isOpen(): boolean {
    return this.#open;
  }

  show(entry: ReleaseEntry | null, config: ClickReleaseConfig): void {
    if (this.#open) return;
    this.#open = true;

    const placement = config.banner.placement;
    const banner = el('div', { class: `cr-banner cr-banner--${placement}`, role: 'status' });

    const text = el('div', { class: 'cr-banner__text' });
    text.append(el('strong', { class: 'cr-banner__title' }, [entry?.title ?? config.title]));
    banner.append(text);

    const cta = el('button', { class: 'cr-banner__cta', type: 'button' }, ['Read more']);
    cta.addEventListener('click', () => this.#onOpenChangelog());
    banner.append(cta);

    if (config.banner.dismissible) {
      const closeBtn = el('button', { class: 'cr-banner__close', type: 'button', 'aria-label': 'Dismiss' }, ['×']);
      closeBtn.addEventListener('click', () => this.hide());
      banner.append(closeBtn);
    }

    this.#root.append(banner);
    this.#node = banner;

    // Reserve the banner's height before it slides in, so content isn't bumped abruptly.
    this.#reserve(placement, banner.offsetHeight);
    enter(banner);
  }

  hide(): void {
    if (!this.#open || !this.#node) return;
    this.#open = false;
    const node = this.#node;
    this.#node = null;
    this.#release();
    leave(node, 'is-open', () => node.remove());
  }

  destroy(): void {
    this.#release(true);
    this.#node?.remove();
    this.#node = null;
    this.#open = false;
  }

  #reserve(side: BannerPlacement, height: number): void {
    const root = document.documentElement;
    root.style.transition = RESERVE_TRANSITION;
    if (side === 'top') root.style.paddingTop = `${height}px`;
    else root.style.paddingBottom = `${height}px`;
    this.#reservedSide = side;
  }

  #release(clearTransition = false): void {
    const root = document.documentElement;
    if (this.#reservedSide === 'top') root.style.paddingTop = '';
    else if (this.#reservedSide === 'bottom') root.style.paddingBottom = '';
    this.#reservedSide = null;
    if (clearTransition) root.style.transition = '';
  }
}
